import SnackBarProvider from 'components/SnackBarProvider';
import { GFO_TOKEN, getBaseURL } from 'constants/index';
import './App.css';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ErrorBoundary } from '@sentry/react';
import AddressProvider from 'components/AddressProvider';
import AlertProvider from 'components/AlertProvider';
import AppProvider from 'components/AppProvider';
import AppThemeProvider from 'components/AppThemeProvider';
import AuthProvider from 'components/AuthProvider';
import CartProvider from 'components/CartProvider';
import CateringProvider from 'components/CateringProvider';
import JSONLD from 'components/JSONLD';
import OverlayProvider from 'components/OverlayProvider';
import RedirectProvider from 'components/RedirectProvider';
import ErrorFallback from 'pages/ErrorFallback';
import React, { useEffect } from 'react';
import { Router, useHistory } from 'react-router-dom';
import Routes from 'Routes';
import { createCookie, getQueryStringValue } from 'utils';
import { isApp } from 'utils/app';
import { historyData } from 'utils/history';
import { SessionJsonStorage } from 'utils/storage';
import AppUpdateOverlay from './AppUpdateOverlay';
import MetaTags from './MetaTag';
import useStructuredData from './useStructuredData';

function App() {
  const history = useHistory();
  const structuredData = useStructuredData();
  const baseURL = getBaseURL();

  useEffect(() => {
    const clear = history.listen((location, action) => {
      let { pathStack = [] } = historyData.get();
      if (action === 'PUSH') {
        pathStack = [...pathStack, { action, ...location }];
      }
      if (action === 'POP' && pathStack.length > 0) {
        pathStack = [...pathStack.slice(-1)];
      }
      pathStack = [...pathStack.slice(-1), { action, ...location }];
      historyData.update({ lastAction: action, pathStack });
    });
    return clear;
  }, [history]);

  useEffect(() => {
    const GFOToken = getQueryStringValue(GFO_TOKEN);
    if (GFOToken) {
      createCookie({
        name: GFO_TOKEN,
        value: GFOToken,
        expireTimeInMinutes: 30,
      });
    }
  }, []);

  useEffect(() => {
    const baseURL = getQueryStringValue('baseURL');
    if (baseURL) {
      SessionJsonStorage.set({
        key: 'baseURL',
        value: baseURL,
      });
      window.location.replace(window.location.pathname);
    }
  }, []);

  return (
    <AppProvider>
      <AppThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <RedirectProvider>
            <AuthProvider>
              <AlertProvider>
                <OverlayProvider>
                  <SnackBarProvider>
                    <AddressProvider>
                      <CartProvider>
                        <CateringProvider>
                          <MetaTags />
                          {!isApp() && baseURL.customized && (
                            <div
                              style={{
                                background: 'crimson',
                                color: '#fff',
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                zIndex: 10000,
                                fontSize: 12,
                              }}
                            >
                              baseURL:
                              {' '}
                              {baseURL.url}
                            </div>
                          )}
                          <ErrorBoundary fallback={<ErrorFallback />}>
                            <AppUpdateOverlay />
                            <CartProvider.CartOverlay />
                            <Router history={history}>
                              <JSONLD data={structuredData} />
                              <Routes />
                            </Router>
                          </ErrorBoundary>
                        </CateringProvider>
                      </CartProvider>
                    </AddressProvider>
                  </SnackBarProvider>
                </OverlayProvider>
              </AlertProvider>
            </AuthProvider>
          </RedirectProvider>
        </LocalizationProvider>
      </AppThemeProvider>
    </AppProvider>
  );
}

export default App;
